import * as React from 'react'
import { useStoreState, useStoreActions } from 'easy-peasy'
import './App.scss'
import moment from 'moment'
import toaster from 'react-hot-toast'
import * as yup from 'yup'
import useFormHelper from '../../hooks/useFormHelper.js'

import Header from '../../components/Header/Header'

const { useState, useEffect, useCallback } = React

const schema = yup.object().shape({
  url: yup.string().required('Missing url address'),
  expiryDate: yup.string()
})

const toDateStr = (d) => d.toISOString().split('T')[0]
const today = toDateStr(new Date())
const maxDate = toDateStr(new Date(new Date().setFullYear(new Date().getFullYear() + 100)))

const defaultExpiry = (daysFromNow = 180) => {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return toDateStr(d)
}

const App = () => {
  const links = useStoreState(state => state.link.all || [])
  const getLinks = useStoreActions(actions => actions.link.getLinks)
  const createLink = useStoreActions(actions => actions.link.createLink)
  const updateLink = useStoreActions(actions => actions.link.updateLink)
  const deleteLink = useStoreActions(actions => actions.link.deleteLink)
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [editDate, setEditDate] = useState('')

  const { formValues, setFormValue, handleSubmit, showError } = useFormHelper({
    schema: schema,
    initialState: { url: '', expiryDate: defaultExpiry() },
    ErrorWrapper: ({ children }) => <p className="small error">{children}</p>
  })

  useEffect(() => {
    setLoading(true)
    getLinks({ options: { completed: () => setLoading(false) } })
  }, [reload])

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return
    setLoading(true)
    setDeleteTarget(null)
    deleteLink({
      id: deleteTarget._id,
      options: {
        completed: success => {
          setLoading(false)
          if (success) {
            toaster.success('Link deleted')
            setReload(Math.random())
          }
        }
      }
    })
  }, [deleteTarget, deleteLink])

  const dateToDays = (dateStr) => {
    const target = new Date(dateStr + 'T00:00:00')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diff = Math.round((target - today) / (1000 * 3600 * 24))
    return Math.max(diff, 1)
  }

  const openEdit = (link) => {
    setEditTarget(link)
    if (!link.expires) return setEditDate('')
    const current = moment(link.expires).format('YYYY-MM-DD')
    setEditDate(current < moment().format('YYYY-MM-DD') ? defaultExpiry() : current)
  }

  const confirmEdit = () => {
    if (!editTarget) return
    setLoading(true)
    setEditTarget(null)
    updateLink({
      id: editTarget._id,
      expires: editDate || null,
      options: {
        completed: success => {
          setLoading(false)
          if (success) {
            toaster.success('Expiry updated')
            setReload(Math.random())
          }
        }
      }
    })
  }

  return (
    <section className="app-page">
      <Header />
      <div className="body-content">
        <div className="new-link-creator">
          <div className="creator-label">Shorten a new link</div>
          <form
            className={`normal-form no-gap ${loading ? 'submitting' : ''}`}
            action=""
            onSubmit={e => {
              e.preventDefault()
              handleSubmit(() => {
                setLoading(true)
                createLink({
                  url: formValues.url,
                  days: formValues.expiryDate ? dateToDays(formValues.expiryDate) : null,
                  options: {
                    completed: short => {
                      setLoading(false)
                      if (short) {
                        toaster.success(`Link created: ${new URL(short, window.location.origin).href}`, {
                          duration: 5000
                        })
                        setReload(Math.random())
                      }
                    }
                  }
                })
              })
            }}
          >
            <div className="row inline">
              <input
                placeholder="Paste your long URL here..."
                type="text"
                autoComplete="off"
                onChange={e => setFormValue('url', e.target.value)}
              />
              <div className="expiry-input">
                <label className="expiry-label" title="Clear the date for a link that never expires">Expires</label>
                <input
                  type="date"
                  value={formValues.expiryDate}
                  min={today}
                  max={maxDate}
                  onChange={e => setFormValue('expiryDate', e.target.value)}
                />
              </div>
              <button className="action">Shorten</button>
            </div>
            <div>{showError('url')}</div>
          </form>
        </div>
        <div className="links-table">
          <div className="sticky-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Short Link</th>
                  <th className="expanding">Destination</th>
                  <th>Expires</th>
                  <th>Visits</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {!links.length && (
                  <tr className="empty-state">
                    <td colSpan={6}>No links yet. Create your first one above.</td>
                  </tr>
                )}
                {links.map((link, idx) => {
                  const isExpired = !!link.expires && new Date(link.expires) < new Date()
                  return (
                    <tr key={idx} className={isExpired ? 'expired' : ''}>
                      <td>{moment(link.created).format('DD/MM/YYYY')}</td>
                      <td
                        className="clickable"
                        onClick={e => {
                          navigator.clipboard.writeText(new URL(e.target.innerText, window.location.origin).href)
                          toaster.success('Copied to clipboard')
                        }}
                      >
                        {link.short}
                      </td>
                      <td
                        className="clickable"
                        onClick={() => {
                          navigator.clipboard.writeText(link.url)
                          toaster.success('Copied to clipboard')
                        }}
                      >
                        {decodeURI(link.url)}
                      </td>
                      <td
                        className={`expiry-date editable ${isExpired ? 'expired-text' : ''}`}
                        onClick={() => openEdit(link)}
                        title="Click to change expiry"
                      >
                        {link.expires ? moment(link.expires).format('DD/MM/YYYY') : 'Never'}
                        <svg className="edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </td>
                      <td className="visits-count">{link.visits || 0}</td>
                      <td className="actions-col">
                        <button className="delete-btn" onClick={() => setDeleteTarget(link)} title="Delete link">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete link</h3>
            <p>
              Are you sure you want to delete <strong>{deleteTarget.short}</strong>?
            </p>
            <p className="modal-url">{decodeURI(deleteTarget.url)}</p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="modal-btn danger" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <h3>Change expiry</h3>
            <p>
              Set new expiry for <strong>{editTarget.short}</strong>
            </p>
            <p className="modal-url">{decodeURI(editTarget.url)}</p>
            <p className="modal-current-expiry">
              Current: {editTarget.expires ? moment(editTarget.expires).format('DD/MM/YYYY') : 'Never'}
            </p>
            <div className="modal-input-row">
              <span>New expiry</span>
              <input
                type="date"
                value={editDate}
                min={today}
                  max={maxDate}
                onChange={e => setEditDate(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && confirmEdit()}
              />
            </div>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setEditTarget(null)}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={confirmEdit}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default App
